import { useEffect, useRef, useState } from 'react';

const MIN_WIDTH = 520;
const DEFAULT_WIDTH = 680;
const MAX_WIDTH = 1200;
const MOBILE_WIDTH = 600;
const STORAGE_KEY = 'dashboard-user-drawer-width';
const remember = width => { try { localStorage.setItem(STORAGE_KEY, String(width)); } catch { /* Resizing works when browser storage is unavailable. */ } };
const initialWidth = () => {
    try { const saved = Number(localStorage.getItem(STORAGE_KEY)); return Number.isFinite(saved) && saved > 0 ? saved : DEFAULT_WIDTH; }
    catch { return DEFAULT_WIDTH; }
};

export default function useDrawerResize() {
    const [preferredWidth, setPreferredWidth] = useState(initialWidth);
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
    const [dragging, setDragging] = useState(false);
    const drag = useRef(null);
    const latestWidth = useRef(preferredWidth);
    const mobile = viewportWidth <= MOBILE_WIDTH;
    const minimum = mobile ? viewportWidth : MIN_WIDTH;
    const maximum = mobile ? viewportWidth : Math.min(MAX_WIDTH, viewportWidth - 48);
    const width = mobile ? viewportWidth : Math.min(maximum, Math.max(minimum, preferredWidth));
    const bounds = useRef({ minimum, maximum, mobile });
    bounds.current = { minimum, maximum, mobile };

    useEffect(() => {
        const resized = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', resized);
        return () => window.removeEventListener('resize', resized);
    }, []);
    useEffect(() => {
        if (!dragging) return;
        const oldCursor = document.body.style.cursor, oldSelection = document.body.style.userSelect;
        document.body.style.cursor = 'ew-resize'; document.body.style.userSelect = 'none';
        const move = event => {
            const session = drag.current;
            if (!session || event.pointerId !== session.pointerId || bounds.current.mobile) return;
            const next = Math.min(bounds.current.maximum, Math.max(bounds.current.minimum, session.width + session.x - event.clientX));
            latestWidth.current = next; setPreferredWidth(next);
        };
        const finish = event => {
            if (!drag.current || (event?.pointerId != null && event.pointerId !== drag.current.pointerId)) return;
            remember(latestWidth.current);
            drag.current = null; setDragging(false);
        };
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', finish);
        document.addEventListener('pointercancel', finish);
        window.addEventListener('blur', finish);
        return () => {
            document.removeEventListener('pointermove', move);
            document.removeEventListener('pointerup', finish);
            document.removeEventListener('pointercancel', finish);
            window.removeEventListener('blur', finish);
            document.body.style.cursor = oldCursor; document.body.style.userSelect = oldSelection;
        };
    }, [dragging]);
    const setWidth = requested => {
        const next = Math.min(maximum, Math.max(minimum, requested));
        latestWidth.current = next; setPreferredWidth(next); remember(next);
    };
    const start = event => {
        if (mobile || event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.focus({ preventScroll: true });
        event.currentTarget.setPointerCapture?.(event.pointerId);
        drag.current = { pointerId: event.pointerId, x: event.clientX, width };
        latestWidth.current = width; setDragging(true);
    };
    const keyboard = event => {
        const requested = event.key === 'ArrowLeft' ? width + 24 : event.key === 'ArrowRight' ? width - 24 : event.key === 'Home' ? minimum : event.key === 'End' ? maximum : null;
        if (requested == null || mobile) return;
        event.preventDefault(); event.stopPropagation(); setWidth(requested);
    };
    return { width, dragging, mobile, handleProps: {
        role: 'separator', tabIndex: 0, 'aria-label': '상세 패널 너비 조절', 'aria-orientation': 'vertical',
        'aria-valuemin': minimum, 'aria-valuemax': maximum, 'aria-valuenow': width, 'aria-valuetext': `${width}픽셀`,
        title: '좌우로 드래그해 너비 조절 · 더블 클릭으로 기본 너비',
        onPointerDown: start, onKeyDown: keyboard, onDoubleClick: () => setWidth(DEFAULT_WIDTH),
    } };
}
